import { type LabelComponentType, getLabelComponent } from "../components/label/Label";
import {
    type GetLabelType,
    type GetOptionalLabelType,
    type UseLabelType,
    getLabelFn,
    getOptionalLabelFn,
    getUseLabel,
    getUseOptionalLabel,
} from "../hooks/useLabel";
import type { CommonSettingsType } from "../settings/fetchCommonSettings";
import { T1Labels } from "./t1labels";

// ----------------------------------------------------------------------
// Accessibility labels
// ----------------------------------------------------------------------
export const accessibilityLabels = {
    ariaSliderMinValue: "min value: {value}",
    ariaSliderMaxValue: "max value: {value}",
    ariaSliderMinValueTitleLabel: "Slider {value} minimum",
    ariaSliderMaxValueTitleLabel: "Slider {value} maximum",
    ariaSliderLeft: "Slider Left",
    ariaSliderRight: "Slider Right",
    ariaScrollLeft: "Scroll Left",
    ariaScrollRight: "Scroll Right",
    ariaInfo: "Read Info",
    ariaClose: "Close",
    ariaRemove: "Remove",
    ariaBreadcrumbs: "Breadcrumbs",
    ariaToggleEtaTooltip: "Toggle ETA Tooltip",
};

export const commonLabels = {
    ...T1Labels,
    ...accessibilityLabels,
    close: "Close",
    quotes: "Quotes",
    dimensions: "Dimensions",
    uscExclVatCashPriceFormat: "%s ex.&nbsp;VAT", // Used specifically in UCL components to render Ex VAT formatting. %s is used as this is also used for price replacements in the website.
    uscInclVatCashPriceFormat: "%s", // Used specifically in UCL components to render Incl VAT formatting. By default, this label should only render the price. Only when localised, should it render the inclVat label.
    uscCashPriceFormat: "%s", // Generic cash price format. Yields to exclVat/inclVat if present and applicable.
    uscExclVatCashPriceDisclaimer: "",
    uscInclVatCashPriceDisclaimer: "",
    uscCashPriceDisclaimer: "", // Generic cash price disclaimer
    uscAccessoryCashPriceDisclaimer: "",
    uscServiceProductCashPriceDisclaimer: "",
    uscNedcDisclaimer: "",
    uscWltpDisclaimer: "",
    uscNedcNotAvailableDisclaimer: "",
    uscEfficiencyClassNotAvailableDisclaimer: "",
    uscPageFinanceDisclaimer: "",
    uscPrivatelySold: "Privately sold",
    uscApproved: "Approved",
    uscNonApproved: "Non-approved",
    dualCurrencyDisclaimer: "Dual currency rate: {value}",
    tyreInfoFalse: "",
    tyreInfoTrue: "",
    printPrintedOn: "Printed on",
    genericOrErrorTitle: "Oops, something went wrong and we couldn't process your request.",
    soldOut: "Sold out", // Sold out
    limitedStock: "Limited stock", // Limited stock
    availableForOnlineSale: "Available online", // Available online
    availableForOnlineLeasing: "Available for leasing", // Available for leasing
    uscManufacturerWarrantyKmLimit: "KM Limit: {value}",
    uscManufacturerWarrantyValidUntil: "Valid until: {value}",
    uscManufacturerWarrantyUnspecified: "Unspecified",
    uscManufacturerWarrantyNone: "None",
    uscManufacturerWarrantyExpired: "Expired",
    uscSpecLabelYes: "Yes",
    uscSpecLabelNo: "No",

    etaTooltip: "",
    etaTooltipTitle: "",

    // ETA labels differ from the other labels in format (SNAKE_CASE vs camelCase) because we are reusing the labels from Trinity
    ETA_ONE_WEEK: "Within 1 week",
    ETA_TWO_WEEKS: "Within 2 weeks",
    ETA_THREE_WEEKS: "Within 3 weeks",
    ETA_JANUARY: "within january",
    ETA_FEBRUARY: "within february",
    ETA_MARCH: "within march",
    ETA_APRIL: "within april",
    ETA_MAY: "within may",
    ETA_JUNE: "within june",
    ETA_JULY: "within july",
    ETA_AUGUST: "within august",
    ETA_SEPTEMBER: "within september",
    ETA_OCTOBER: "within october",
    ETA_NOVEMBER: "within november",
    ETA_DECEMBER: "within december",
};

export type CommonLabelType = keyof typeof commonLabels;
type CommonLabelParamType = Partial<Record<"productName" | "value", string | number>>;

export type CommonStateType = {
    commonSettings: CommonSettingsType;
};

export const getCommonLabel = getLabelFn(
    (state: CommonStateType) => state.commonSettings.labels,
    (state: CommonStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as GetLabelType<CommonStateType, CommonLabelType, CommonLabelParamType>;

export const getCommonOptionalLabel = getOptionalLabelFn(
    (state: CommonStateType) => state.commonSettings.labels,
    (state: CommonStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as GetOptionalLabelType<CommonStateType, CommonLabelType, CommonLabelParamType>;

export const useCommonLabel = getUseLabel(
    (state: CommonStateType) => state.commonSettings.labels,
    (state: CommonStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as UseLabelType<CommonLabelType, CommonLabelParamType>;

export const useCommonOptionalLabel = getUseOptionalLabel(
    (state: CommonStateType) => state.commonSettings.labels,
    (state: CommonStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as UseLabelType<CommonLabelType, CommonLabelParamType>;

export const CommonLabel = getLabelComponent(useCommonLabel) as LabelComponentType<
    CommonLabelType,
    CommonLabelParamType
>;
